import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Download,
  Loader2,
  AlertCircle,
  ExternalLink,
  Clock,
  FileSpreadsheet,
  FileCode,
  Package,
  Paintbrush,
  Link,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { feedWatchService } from '../../services/feedWatch';
import type { FeedExport, ExportFormat, ExportStatus, CanvasDesign, FeedType } from '../../services/feedWatch';
import { FEED_TYPE_FORMATS } from '../../services/feedWatch';

// ── Status badge config ─────────────────────────────────────────

const EXPORT_STATUS_CONFIG: Record<ExportStatus, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
  processing: { label: 'Processing', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  completed: { label: 'Completed', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
  failed: { label: 'Failed', className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

function ExportStatusBadge({ status }: { status: ExportStatus }) {
  const config = EXPORT_STATUS_CONFIG[status];
  return (
    <Badge variant="outline" className={config.className}>
      {(status === 'queued' || status === 'processing') && (
        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
      )}
      {config.label}
    </Badge>
  );
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FormatIcon({ format }: { format: string }) {
  const Icon = format === 'xml' ? FileCode : FileSpreadsheet;
  return (
    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-primary" />
    </div>
  );
}

// ── Copy button ─────────────────────────────────────────────────

function CopyUrlButton({ url, label }: { url: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Button
      variant="outline"
      size="sm"
      className={`rounded-lg ${copied ? 'border-emerald-500 text-emerald-600' : ''}`}
      onClick={handleCopy}
      title={url}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-500" />
      ) : (
        <Link className="h-3.5 w-3.5 mr-1.5" />
      )}
      {copied ? 'Copied' : label}
    </Button>
  );
}

// ── Download button (blob fetch for cross-origin) ───────────────

function DownloadButton({ url, filename }: { url: string; filename: string }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button variant="outline" size="sm" className="rounded-lg" onClick={handleDownload} disabled={isDownloading}>
      {isDownloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
      {isDownloading ? 'Downloading...' : 'Download'}
    </Button>
  );
}

// ── Main ExportManager ──────────────────────────────────────────

interface ExportManagerProps {
  feedId: string;
  feedStatus: string;
  feedType?: FeedType;
}

export function ExportManager({ feedId, feedStatus, feedType }: ExportManagerProps) {
  const allowedFormats: ExportFormat[] = feedType ? FEED_TYPE_FORMATS[feedType] : ['xml', 'csv', 'tsv'];

  const [exports, setExports] = useState<FeedExport[]>([]);
  const [designs, setDesigns] = useState<CanvasDesign[]>([]);
  const [format, setFormat] = useState<ExportFormat>(allowedFormats[0]);
  const [selectedDesignId, setSelectedDesignId] = useState<string>('original');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const activeExportRef = useRef<string | null>(null);

  const loadExports = useCallback(async () => {
    try {
      const list = await feedWatchService.listExports(feedId);
      const items = Array.isArray(list) ? list : [];

      // The list endpoint may not return download_url/public_url.
      // Hydrate completed exports by fetching their individual details.
      const hydrated = await Promise.all(
        items.map((exp) =>
          exp.status === 'completed' && !exp.download_url
            ? feedWatchService.getExport(feedId, exp.id).catch(() => exp)
            : exp
        )
      );

      setExports(hydrated);
    } catch {
      // might not have exports yet
    }
  }, [feedId]);

  const loadDesigns = useCallback(async () => {
    try {
      const result = await feedWatchService.listDesigns(feedId);
      setDesigns(Array.isArray(result) ? result : []);
    } catch {
      // no designs
    }
  }, [feedId]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadExports(), loadDesigns()]);
      setIsLoading(false);
    };
    init();
  }, [loadExports, loadDesigns]);

  const startPolling = useCallback((exportId: string) => {
    activeExportRef.current = exportId;
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const exp = await feedWatchService.getExport(feedId, exportId);
        setExports((prev) =>
          prev.map((e) => (e.id === exportId ? exp : e))
        );
        if (exp.status === 'completed' || exp.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          activeExportRef.current = null;
        }
      } catch {
        // Keep polling
      }
    }, 2000);
  }, [feedId]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!allowedFormats.includes(format)) {
      setFormat(allowedFormats[0]);
    }
  }, [feedType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const active = exports.find(
      (e) => e.status === 'queued' || e.status === 'processing'
    );
    if (active && activeExportRef.current !== active.id) {
      startPolling(active.id);
    }
  }, [exports, startPolling]);

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      setError(null);
      const designId = selectedDesignId !== 'original' ? selectedDesignId : undefined;
      const exp = await feedWatchService.createExport(feedId, format, designId);
      setExports((prev) => [exp, ...prev]);
      startPolling(exp.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const canExport = feedStatus === 'ready';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Create export card */}
      <Card className="border-2 rounded-xl p-6">
        <p className="text-sm font-semibold mb-4">Generate Export</p>
        <div className="flex items-center gap-4">
          <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
            <SelectTrigger className="w-[160px] h-11 rounded-xl border-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allowedFormats.includes('xml') && (
                <SelectItem value="xml">
                  <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4" />
                    XML
                  </div>
                </SelectItem>
              )}
              {allowedFormats.includes('csv') && (
                <SelectItem value="csv">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    CSV
                  </div>
                </SelectItem>
              )}
              {allowedFormats.includes('tsv') && (
                <SelectItem value="tsv">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    TSV
                  </div>
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {/* Canvas design selector */}
          {designs.length > 0 && (
            <Select value={selectedDesignId} onValueChange={setSelectedDesignId}>
              <SelectTrigger className="w-[220px] h-11 rounded-xl border-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20">
                <div className="flex items-center gap-2">
                  <Paintbrush className="h-4 w-4 text-muted-foreground shrink-0" />
                  <SelectValue placeholder="Original images" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="original">Original images</SelectItem>
                {designs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} ({d.width}x{d.height})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button
            className="rounded-lg h-11"
            onClick={handleCreate}
            disabled={!canExport || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Generate Export
          </Button>

          {!canExport && (
            <span className="text-sm text-muted-foreground">
              Feed must be in "ready" status to export.
            </span>
          )}
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2.5 text-sm text-destructive bg-destructive/10 rounded-xl px-5 py-3.5 border border-destructive/20">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Export history */}
      {exports.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed rounded-xl">
          <Package className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-1">No exports yet</h3>
          <p className="text-muted-foreground">
            Generate your first export to see it here.
          </p>
        </Card>
      ) : (
        <Card className="border-2 shadow-lg rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b bg-muted/30">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-semibold">Export History</span>
              <Badge variant="secondary" className="text-xs tabular-nums">
                {exports.length}
              </Badge>
            </div>
          </div>

          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/20">
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Format</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Products</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Size</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {exports.map((exp) => (
                <tr key={exp.id} className="hover:bg-muted/30 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <FormatIcon format={exp.format} />
                      <span className="text-sm font-medium uppercase">{exp.format}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <ExportStatusBadge status={exp.status} />
                  </td>
                  <td className="px-6 py-4 text-sm tabular-nums">
                    {exp.product_count != null ? exp.product_count.toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground tabular-nums">
                    {formatFileSize(exp.file_size_bytes)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      {new Date(exp.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {exp.status === 'completed' && (
                      <div className="flex items-center gap-2">
                        {exp.download_url && (
                          <DownloadButton url={exp.download_url} filename={`export.${exp.format}`} />
                        )}
                        {exp.public_url && (
                          <CopyUrlButton url={exp.public_url} label="Feed URL" />
                        )}
                      </div>
                    )}
                    {exp.status === 'failed' && exp.error_message && (
                      <span className="text-xs text-destructive line-clamp-1">{exp.error_message}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
